// A menu, /client/:clientId/menu/:menuId: Cucina and Bevande as filter tabs (?tab=), a search over the whole menu
// (?q=), collapsible categories with one level of sub-categories, a Riordina mode, "Aggiungi" at each category's foot,
// and the publish bar while there are unpublished changes or the website is rebuilding. The open item is ?item=, a new
// one ?new=<categoryId>, both in ItemDrawer; a new category is ?newCategory=<tab>, from "Aggiungi categoria" at the
// tab's foot. Each category's ⋮ renames it in its header or hides it, and Riordina moves categories among their
// siblings by their handles. A session that only reads gets no add, no ⋮, no Riordina and no publish bar.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { PlusIcon, CloseIcon, SearchIcon } from "@/components/icons";
import { PanelFilterTabs, type PanelFilterOption } from "@/components/ui/PanelFilterTabs";
import { PanelHeaderAction } from "@/components/ui/PanelHeaderAction";
import { usePublishTrail } from "@/core/navigation/TrailDisplayContext";
import { useAppStore } from "@/core/store/useAppStore";
import { WorkspacePanel } from "@/domains/Ideas/components/WorkspacePanel";
import { cn } from "@/utils/cn";
import { useLocale } from "@/hooks/useLocale";
import { CategoryMenu, CategoryRename } from "../components/CategoryControls";
import { CategorySection, GroupedRows } from "../components/CategorySection";
import { ItemDrawer } from "../components/ItemDrawer";
import { ItemRow } from "../components/ItemRow";
import { NewCategoryDrawer } from "../components/NewCategoryDrawer";
import { PUBLISH_BAR_CLEARANCE, PublishBar } from "../components/PublishBar";
import { ReorderHandle, ReorderList, useReorder } from "../components/ReorderList";
import { UndoToast, type UndoAction } from "../components/UndoToast";
import { plural, publishBar, saveError } from "../format";
import { useMenu, useMenuMutations, useMenuVersion } from "../hooks/useMenu";
import { menuLayout, type CategoryBlock } from "../layout";
import { mergeSiblingOrder, searchItems } from "../rules";
import type { DeployStatus, MenuCategory } from "../types";

type Tab = "cucina" | "bevande";
const isTab = (value: string | null): value is Tab => value === "cucina" || value === "bevande";

/** How long "Sito aggiornato" stays in the bar once the build has finished. */
const SITE_DONE_MS = 6000;

/** What the foot of a category adds: a dish, a wine, or another drink. */
function addLabel(category: MenuCategory, isWine: boolean): string {
  if (!category.isDrink) return "Aggiungi piatto";
  return isWine ? "Aggiungi vino" : "Aggiungi bevanda";
}

/**
 * The website build the bar follows: the version just published here, or, on arrival, the last one while its build
 * still runs or has failed. A build that succeeded before this visit says nothing.
 */
function useWatchedDeploy(menuId: string, latest: { id: string; deployStatus: DeployStatus } | null | undefined) {
  const [watched, setWatched] = useState<string | null>(null);
  const arrived = useRef(false);
  useEffect(() => {
    if (arrived.current || latest === undefined) return;
    arrived.current = true;
    if (latest && (latest.deployStatus === "running" || latest.deployStatus === "failed")) setWatched(latest.id);
  }, [latest]);
  const version = useMenuVersion(menuId, watched);
  const deploy: DeployStatus | null = watched === null
    ? null
    : version.data?.deployStatus ?? (latest?.id === watched ? latest.deployStatus : "running");
  useEffect(() => {
    if (deploy !== "succeeded") return undefined;
    const timer = window.setTimeout(() => setWatched(null), SITE_DONE_MS);
    return () => window.clearTimeout(timer);
  }, [deploy]);
  return { deploy, watch: setWatched };
}

export function MenuRoute() {
  const { clientId = "", menuId = "" } = useParams();
  const navigate = useNavigate();
  const { t } = useLocale();
  const addToast = useAppStore((state) => state.addToast);
  const [params, setParams] = useSearchParams();
  const rawTab = params.get("tab");
  const tab: Tab = isTab(rawTab) ? rawTab : "cucina";
  const query = params.get("q") ?? "";
  const searching = query.trim() !== "";
  const itemId = params.get("item");
  const newCategoryId = params.get("new");
  const rawNewTab = params.get("newCategory");
  const newCategoryTab: Tab | null = isTab(rawNewTab) ? rawNewTab : null;

  const { data: menu, isLoading, error } = useMenu(menuId);
  const mutations = useMenuMutations(menuId);
  const layout = useMemo(() => menuLayout(menu), [menu]);
  const canEdit = menu?.canEdit ?? false;
  const { deploy, watch } = useWatchedDeploy(menuId, menu ? menu.latestVersion : undefined);

  const [toggled, setToggled] = useState<Record<string, boolean>>({});
  const [reorderMode, setReorderMode] = useState(false);
  const [reordering, setReordering] = useState<string | null>(null);
  /** The category whose name is an input in its header. */
  const [renaming, setRenaming] = useState<string | null>(null);
  /** Where the open item was opened from, which ‹ › walk: a category's id, or "search". */
  const [walkFrom, setWalkFrom] = useState<string | null>(null);
  const [undo, setUndo] = useState<UndoAction | null>(null);
  const undoCount = useRef(0);
  // True while the open drawer was pushed onto history by this page, so closing it can step back instead of stacking.
  const drawerPushed = useRef(false);
  const newRef = useRef(newCategoryId);
  newRef.current = newCategoryId;
  const newCategoryRef = useRef(newCategoryTab);
  newCategoryRef.current = newCategoryTab;

  usePublishTrail(menu ? [
    { to: `/client/${clientId}`, label: t("client.trail.dashboard") },
    { to: `/client/${clientId}/menu`, label: t("client.menu.menu") },
    { to: `/client/${clientId}/menu/${menuId}`, label: menu.menu.name },
  ] : null);

  const setParam = useCallback((changes: Record<string, string | null>, replace = true) => {
    setParams((current) => {
      const next = new URLSearchParams(current);
      for (const [key, value] of Object.entries(changes)) {
        if (value === null || value === "") next.delete(key);
        else next.set(key, value);
      }
      return next;
    }, { replace });
  }, [setParams]);

  const showUndo = useCallback((message: string, run: () => void) => {
    undoCount.current += 1;
    setUndo({ id: undoCount.current, message, undo: run });
  }, []);
  const dismissUndo = useCallback(() => setUndo(null), []);
  const failed = useCallback((message: string) => addToast({ message, type: "error" }), [addToast]);

  const openItem = (id: string, from: string) => {
    setWalkFrom(from);
    drawerPushed.current = true;
    setParam({ item: id, new: null }, false);
  };
  const openNew = (category: MenuCategory) => {
    setWalkFrom(category.id);
    drawerPushed.current = true;
    setParam({ new: category.id, item: null }, false);
  };
  const openNewCategory = () => {
    drawerPushed.current = true;
    setParam({ newCategory: tab, item: null, new: null }, false);
  };
  const closeDrawer = () => {
    if (drawerPushed.current) {
      drawerPushed.current = false;
      navigate(-1);
    } else setParam({ item: null, new: null, newCategory: null });
  };

  const results = searching ? searchItems(layout.ordered, query) : [];
  const blockOf = (id: string): CategoryBlock | undefined => layout.blocks.find((b) => b.ids.includes(id));
  // A reload with ?item= has no list it came from: the item's category, in display order.
  const from = walkFrom ?? (itemId ? blockOf(itemId)?.category.id ?? null : null);
  const drawerList = from === "search"
    ? results.map((i) => i.id)
    : layout.blocks.find((b) => b.category.id === from)?.ids ?? [];
  const newBlock = newCategoryId ? layout.blocks.find((b) => b.category.id === newCategoryId) ?? null : null;

  const isOpen = (c: MenuCategory) => toggled[c.id] ?? (!c.isDrink && !c.hidden);
  const toggle = (c: MenuCategory) => setToggled((current) => ({ ...current, [c.id]: !isOpen(c) }));
  const leaveReorder = () => {
    setReorderMode(false);
    setReordering(null);
  };

  const tabBlocks = layout.blocks.filter((b) => (tab === "bevande" ? b.category.isDrink : !b.category.isDrink));
  // The tab's root categories move among themselves; the server takes every root, the other tab's where they were.
  const roots = useReorder(tabBlocks.map((b) => b.category.id), (ids) => mutations.reorderCategories.mutate(
    { parentId: null, categoryIds: mergeSiblingOrder(layout.blocks.map((b) => b.category.id), ids) },
    { onError: (e) => failed(saveError(e)) },
  ));
  const dragRoots = reorderMode && reordering === null && tabBlocks.length > 1;
  // The order a drag or a move shows, until the server's arrives; a category that is new, or another tab's, follows it.
  const shownBlocks = [
    ...roots.order.map((id) => tabBlocks.find((b) => b.category.id === id)),
    ...tabBlocks.filter((b) => !roots.order.includes(b.category.id)),
  ].filter((b): b is CategoryBlock => b !== undefined);

  const rename = (category: MenuCategory, name: string) => mutations.patchCategory.mutate(
    { categoryId: category.id, patch: { name } },
    { onError: (e) => failed(saveError(e)) },
  );
  const toggleHidden = (category: MenuCategory) => mutations.patchCategory.mutate(
    { categoryId: category.id, patch: { hidden: !category.hidden } },
    { onError: (e) => failed(saveError(e)) },
  );
  const categoryMenu = (category: MenuCategory) => (
    <CategoryMenu category={category} onRename={() => setRenaming(category.id)} onToggleHidden={() => toggleHidden(category)} />
  );
  const renameInput = (category: MenuCategory) => (
    <CategoryRename name={category.name} onCommit={(name) => rename(category, name)} onDone={() => setRenaming(null)} />
  );
  const bar = publishBar({ pending: menu?.pending ?? 0, canEdit, deploy });
  const tabs: PanelFilterOption[] = [
    { id: "cucina", label: "Cucina" },
    { id: "bevande", label: "Bevande" },
  ];
  const publish = () => mutations.publish.mutate(undefined, {
    onSuccess: (version) => {
      watch(version.id);
      addToast({ message: "Menu pubblicato", type: "success", duration: 3000 });
    },
    onError: (e) => failed(saveError(e)),
  });

  return (
    <div className="flex-1 min-h-0 overflow-y-auto lg:[scrollbar-gutter:stable_both-edges]">
      <div className={`mx-auto flex min-h-full w-full max-w-2xl flex-col px-4 pt-3 lg:px-6 ${bar ? "" : "pb-6"}`}>
        <WorkspacePanel
          title={menu?.menu.name ?? t("client.menu.menu")}
          collapsed={false}
          loading={isLoading}
          actions={canEdit && !searching ? (
            <PanelHeaderAction
              label={reorderMode ? "Fatto" : "Riordina"}
              variant={reorderMode ? "primary" : "secondary"}
              onClick={() => (reorderMode ? leaveReorder() : setReorderMode(true))}
            />
          ) : undefined}
          subheader={
            <PanelFilterTabs
              options={tabs}
              activeId={tab}
              onChange={(id) => {
                if (!isTab(id)) return;
                leaveReorder();
                setParam({ tab: id === "cucina" ? null : id });
              }}
            />
          }
        >
          {error || !menu ? (
            <p className="text-sm text-red-600">{error ? "Non è stato possibile caricare il menu." : ""}</p>
          ) : (
            <div className="space-y-3">
              {!canEdit && (
                <p className="px-1 text-[13px] text-gray-500">Puoi consultare il menu, ma non modificarlo.</p>
              )}
              <div className="relative">
                <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => {
                    leaveReorder();
                    setParam({ q: e.target.value });
                  }}
                  placeholder="Cerca piatti, vini, produttori"
                  aria-label="Cerca nel menu"
                  className="h-[40px] w-full rounded-xl bg-gray-50 pl-9 pr-10 text-[16px] text-gray-900 outline-none placeholder:text-gray-400 focus:bg-white focus:ring-2 focus:ring-indigo-300 [&::-webkit-search-cancel-button]:appearance-none"
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => setParam({ q: null })}
                    aria-label="Cancella la ricerca"
                    className="absolute right-1 top-1/2 grid h-[32px] w-[32px] -translate-y-1/2 place-items-center rounded-lg text-gray-400 hover:text-gray-600"
                  >
                    <CloseIcon className="h-4 w-4" />
                  </button>
                )}
              </div>

              {searching ? (
                <div>
                  <p className="px-1 pb-1 text-[12px] text-gray-500" role="status">
                    {results.length === 0
                      ? `Nessun risultato per “${query.trim()}”.`
                      : `${plural(results.length, "risultato", "risultati")} in tutto il menu`}
                  </p>
                  <div className="divide-y divide-gray-100 px-1">
                    {results.map((item) => (
                      <div key={item.id}>
                        <ItemRow item={item} context={layout.categoryName.get(item.categoryId)} onOpen={() => openItem(item.id, "search")} />
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  {reorderMode && !reordering && (
                    <p className="px-1 text-[13px] text-gray-500">Scegli la categoria da riordinare.</p>
                  )}
                  {tabBlocks.length === 0 && (
                    <p className="px-1 py-3 text-[13px] text-gray-400">Nessuna categoria in {tab === "bevande" ? "Bevande" : "Cucina"}.</p>
                  )}
                  {shownBlocks.map((block) => {
                    const { category, own, children, groups, count, isWine } = block;
                    const active = reordering === category.id;
                    // Wines will list by region (slice 4), so a hand-made order would not last: no Riordina, for its
                    // items or its sub-categories.
                    const inner = !isWine && (own.length > 1 || children.length > 1);
                    return (
                      <div
                        key={category.id}
                        {...(dragRoots ? roots.rowProps(category.id) : {})}
                        className={cn(dragRoots && "cursor-grab select-none", roots.dragging === category.id && "rounded-xl bg-indigo-50/70")}
                      >
                        <CategorySection
                          category={category}
                          count={count}
                          open={active || isOpen(category)}
                          onToggle={() => toggle(category)}
                          dimmed={reordering !== null && !active}
                          editing={renaming === category.id ? renameInput(category) : undefined}
                          actions={reorderMode ? (
                            <>
                              {inner && (
                                <PanelHeaderAction
                                  label={active ? "Fatto" : "Riordina"}
                                  variant={active ? "primary" : "secondary"}
                                  onClick={() => (active ? setReordering(null) : setReordering(category.id))}
                                  ariaLabel={`${active ? "Fatto" : "Riordina"}: ${category.name}`}
                                />
                              )}
                              {dragRoots && <ReorderHandle label={`Sposta categoria ${category.name}`} handle={roots.handleProps(category.id)} />}
                            </>
                          ) : canEdit ? categoryMenu(category) : undefined}
                          footer={canEdit && !reorderMode ? (
                            <button
                              type="button"
                              onClick={() => openNew(category)}
                              aria-label={`${addLabel(category, isWine)} in ${category.name}`}
                              className="mt-1 inline-flex min-h-[40px] items-center gap-1.5 rounded-lg px-1 text-[13px] font-medium text-indigo-700 hover:text-indigo-900"
                            >
                              <PlusIcon className="h-4 w-4" />
                              {addLabel(category, isWine)}
                            </button>
                          ) : undefined}
                        >
                          {active ? (
                            <>
                              {own.length > 1 && (
                                <ReorderList
                                  items={own}
                                  onReorder={(itemIds) => mutations.reorderItems.mutate(
                                    { categoryId: category.id, itemIds },
                                    { onError: (e) => failed(saveError(e)) },
                                  )}
                                />
                              )}
                              {children.length > 1 && (
                                <>
                                  <p className="pb-1 pt-3 text-[12px] font-medium text-gray-500">Sottocategorie</p>
                                  <ReorderList
                                    items={children}
                                    label="Trascina per riordinare le sottocategorie"
                                    moveLabel={(name) => `Sposta categoria ${name}`}
                                    onReorder={(categoryIds) => mutations.reorderCategories.mutate(
                                      { parentId: category.id, categoryIds },
                                      { onError: (e) => failed(saveError(e)) },
                                    )}
                                  />
                                </>
                              )}
                            </>
                          ) : (
                            <GroupedRows
                              groups={groups}
                              renderItem={(item) => <ItemRow item={item} onOpen={() => openItem(item.id, category.id)} />}
                              groupAside={canEdit && !reorderMode ? (group) => {
                                const child = children.find((c) => c.id === group.key);
                                return child ? categoryMenu(child) : null;
                              } : undefined}
                              groupEditing={(group) => {
                                const child = renaming === group.key ? children.find((c) => c.id === group.key) : undefined;
                                return child ? renameInput(child) : null;
                              }}
                            />
                          )}
                        </CategorySection>
                      </div>
                    );
                  })}
                  {canEdit && !reorderMode && (
                    <button
                      type="button"
                      onClick={openNewCategory}
                      className="inline-flex min-h-[40px] items-center gap-1.5 rounded-lg px-1 text-[13px] font-medium text-indigo-700 hover:text-indigo-900"
                    >
                      <PlusIcon className="h-4 w-4" />
                      Aggiungi categoria
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </WorkspacePanel>

        {/* Pushes the publish bar to the bottom of the screen when the menu is shorter than it. */}
        <div className="flex-1" />
        <PublishBar state={bar} publishing={mutations.publish.isPending} onPublish={publish} />
      </div>

      <ItemDrawer
        menu={menu}
        mutations={mutations}
        itemId={itemId}
        newIn={newBlock?.category ?? null}
        newIsWine={newBlock?.isWine ?? false}
        list={drawerList}
        onNavigate={(id) => setParam({ item: id, new: null })}
        onCreated={(id) => {
          // Closed while the item was being made: it stays made, and the drawer stays shut.
          if (newRef.current !== null) setParam({ item: id, new: null });
        }}
        onClose={closeDrawer}
        onUndoable={showUndo}
        onFailed={failed}
      />
      <NewCategoryDrawer
        tab={newCategoryTab}
        mutations={mutations}
        onCreated={(category) => {
          setToggled((current) => ({ ...current, [category.id]: true }));
          // Closed while the category was being made: it stays made, and the drawer stays shut.
          if (newCategoryRef.current !== null) closeDrawer();
        }}
        onClose={closeDrawer}
      />
      <UndoToast action={undo} onDismiss={dismissUndo} offset={bar ? PUBLISH_BAR_CLEARANCE : 16} />
    </div>
  );
}
